import type { MiddlewareState } from '@floating-ui/react-dom';
import { shift, size } from '@floating-ui/react-dom';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { FocusScope } from 'react-aria';
import {
  ColorSwatch,
  ColorSwatchPicker,
  ColorSwatchPickerItem,
  ListBox,
  ListBoxItem,
} from 'react-aria-components';
import * as styles from './SidebarIconPicker.module.scss';
import {
  DEFAULT_SIDEBAR_ICON,
  SIDEBAR_ICONS,
  SidebarSectionIconContainer,
} from './SidebarSectionIcon';
import Button from '../components/Button';
import PopOverPrimitive, {
  usePopOverContext,
} from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import checkmarkIcon from '../icons/non-prefilled/16x16/check.svg';
import { normalizeToHex } from '../utils/colors';
import useShortcut from '../utils/useShortcut';

const PADDING = 20;

export const COLORS = {
  orange: 'E09A00',
  red: 'FF4143',
  rust: 'EF5A33',
  blue: '207AF1',
  green: '05BD8C',
  purple: '7655DA',
  pink: 'EA3C84',
} satisfies Record<string, string>;

interface ColorSwatchCircleProps {
  value?: string;
}

const ColorSwatchCircle = ({ value }: ColorSwatchCircleProps) => (
  <ColorSwatch
    className={classNames(styles.color, {
      [styles.isDefault]: value == null,
    })}
    style={value ? { background: `#${value}` } : undefined}
  />
);

type ColorPicker = { type: 'predefined' } | { type: 'custom'; value: string };

interface SidebarIconPickerPopOverContentProps {
  currentColor: string;
  currentIcon: string;
  onChangeColor: (color: string) => void;
  onSelectIcon: (icon: (typeof SIDEBAR_ICONS)[number]) => void;
  colorPicker: ColorPicker;
  setColorPicker: React.Dispatch<React.SetStateAction<ColorPicker>>;
}

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const SidebarIconPickerPopOverContent = ({
  currentColor,
  currentIcon,
  onChangeColor,
  colorPicker,
  setColorPicker,
  onSelectIcon,
}: SidebarIconPickerPopOverContentProps) => {
  const finalColor =
    colorPicker?.type === 'custom' ? colorPicker.value : currentColor;

  const isCustomColor =
    Object.entries(COLORS).find(
      ([, value]) => value.toLowerCase() === finalColor.toLowerCase(),
    ) == null;

  return (
    <div className={styles.popOverContentInner}>
      {colorPicker.type === 'predefined' && (
        <section className={styles.colorSection}>
          <div className={styles.colorSectionContent}>
            <ColorSwatchPicker
              onChange={(color) =>
                onChangeColor(color.toString('hex').replace('#', ''))
              }
              className={styles.colorSwatchPicker}
              value={`#${currentColor.toLowerCase()}`}
            >
              {Object.entries(COLORS).map(([colorName, colorValue]) => (
                <ColorSwatchPickerItem
                  color={`#${colorValue.toLowerCase()}`}
                  className={styles.colorButton}
                  aria-label={`Color ${colorName}`}
                  key={colorName}
                >
                  <ColorSwatchCircle value={colorValue} />
                </ColorSwatchPickerItem>
              ))}
            </ColorSwatchPicker>
            <Button
              onClick={() =>
                setColorPicker({ type: 'custom', value: currentColor })
              }
              className={classNames(styles.colorButton, {
                [styles.isSelected]: isCustomColor,
              })}
              aria-label="Custom color"
            >
              <ColorSwatchCircle />
            </Button>
          </div>
        </section>
      )}
      {colorPicker.type === 'custom' && (
        <FocusScope contain autoFocus>
          <section className={styles.customColorSection}>
            <div className={styles.colorSectionContent}>
              <label className={styles.colorSectionLabel}>Color code:</label>
              <input
                aria-label="HEX code"
                onChange={(e) => {
                  setColorPicker({
                    type: 'custom',
                    value:
                      normalizeToHex(e.target.value)?.replace('#', '') ??
                      COLORS.orange,
                  });
                }}
                defaultValue={`#${colorPicker.value}`}
                className={styles.hexCodeInput}
              />
              <Button
                variant="link"
                icon={<SVGIcon size={12}>{checkmarkIcon}</SVGIcon>}
                size="small"
                onClick={() => {
                  onChangeColor(colorPicker.value);
                  setColorPicker({ type: 'predefined' });
                }}
              >
                <span>Done</span>
              </Button>
            </div>
          </section>
        </FocusScope>
      )}
      <section className={styles.iconSection}>
        <ListBox
          className={styles.iconsGrid}
          layout="grid"
          defaultSelectedKeys={[currentIcon]}
          selectionBehavior="replace"
          aria-label="Select icon"
        >
          {SIDEBAR_ICONS.map((icon) => (
            <ListBoxItem
              className={styles.iconButton}
              key={icon.name}
              textValue={icon.name}
              aria-label={icon.name}
              id={icon.name}
              onAction={() => {
                onSelectIcon(icon);
                if (colorPicker?.type === 'custom') {
                  onChangeColor(colorPicker.value);
                }
              }}
            >
              <SidebarSectionIconContainer color={finalColor}>
                <SVGIcon>{icon.icon}</SVGIcon>
              </SidebarSectionIconContainer>
            </ListBoxItem>
          ))}
        </ListBox>
      </section>
    </div>
  );
};

interface ComboboxProps {
  isOpen: boolean;
  currentIcon: string;
  children: React.ReactNode;
  disabled?: boolean;
  onOpen: () => void;
}

const Combobox = ({
  isOpen,
  currentIcon,
  children,
  disabled,
  onOpen,
}: ComboboxProps) => {
  const { id } = usePopOverContext();

  return (
    <PopOverPrimitive.Trigger asChild>
      <div
        className={styles.iconPickerButton}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-controls={id}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`${currentIcon} icon`}
        aria-disabled={disabled}
        onKeyDown={(e) => {
          if (['Enter', 'Space'].includes(e.code)) {
            onOpen();
          }
        }}
      >
        {children}
      </div>
    </PopOverPrimitive.Trigger>
  );
};

interface SidebarIconPickerProps {
  boundaryRef: React.RefObject<HTMLElement>;
  onSelectIcon: (icon: (typeof SIDEBAR_ICONS)[number]) => void;
  onChangeColor: (color: string) => void;
  children: React.ReactNode;
  currentColor: string | undefined;
  currentIcon: string | undefined;
  disabled?: boolean;
}

const SidebarIconPicker = ({
  boundaryRef,
  onSelectIcon,
  onChangeColor,
  children,
  currentColor = COLORS.orange,
  currentIcon = DEFAULT_SIDEBAR_ICON.name,
  disabled,
}: SidebarIconPickerProps) => {
  const [colorPicker, setColorPicker] = useState<
    { type: 'predefined' } | { type: 'custom'; value: string }
  >({ type: 'predefined' });
  const [isOpen, setIsOpen] = useState(false);

  if (isOpen && disabled) {
    setIsOpen(false);
  }

  const getAdditionalMiddleware = useCallback(() => {
    const boundary = boundaryRef.current;
    if (!isOpen || !boundary) return [];
    return [
      shift({
        boundary,
        padding: PADDING,
      }),
      size({
        apply({
          elements,
          availableWidth,
        }: MiddlewareState & { availableWidth: number }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.max(0, availableWidth - 2 * PADDING)}px`,
          });
        },
      }),
    ];
  }, [boundaryRef, isOpen]);

  useShortcut(
    'esc',
    (e) => {
      e.preventDefault();
      setIsOpen(false);
    },
    { global: true, skip: !isOpen },
  );

  return (
    <PopOverPrimitive
      offset={8}
      trigger="click"
      placement="bottom"
      getAdditionalMiddleware={getAdditionalMiddleware}
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <Combobox
        isOpen={isOpen}
        currentIcon={currentIcon}
        onOpen={() => setIsOpen(true)}
        disabled={disabled}
      >
        {children}
      </Combobox>
      <PopOverPrimitive.Floating role="dialog" noPortal>
        <FocusScope contain restoreFocus autoFocus>
          <PopOverPrimitive.Content className={styles.popOverContent}>
            <SidebarIconPickerPopOverContent
              currentColor={currentColor}
              currentIcon={currentIcon}
              onChangeColor={onChangeColor}
              colorPicker={colorPicker}
              setColorPicker={setColorPicker}
              onSelectIcon={(icon) => {
                onSelectIcon(icon);
                setIsOpen(false);
              }}
            />
          </PopOverPrimitive.Content>
        </FocusScope>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default SidebarIconPicker;
